import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Bar,
  Cell,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailyMetricsAbsoluteGrowthPoint } from "./dailyMetricsChart";
import { formatChileanDateWithWeekday } from "../../utils/dateUtils";

const PEAK_BAR_COLOR = "#4a3ab8";

type Props = {
  data: DailyMetricsAbsoluteGrowthPoint[];
  height?: number;
  barColor: string;
  lineColor: string;
  incrementalLegend: string;
  cumulativeLegend: string;
  incrementalTooltipLabel: string;
  cumulativeTooltipLabel: string;
  onDayClick?: (date: string) => void;
  hoverAnimate?: boolean;
};

function ymd(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : undefined;
}

function dateFromChartIndex(
  data: DailyMetricsAbsoluteGrowthPoint[],
  index: unknown,
): string | undefined {
  if (index == null || index === "") return undefined;
  const i = typeof index === "number" ? index : Number(index);
  if (!Number.isInteger(i) || i < 0) return undefined;
  return ymd(data[i]?.date);
}

function dateFromNearestBar(
  container: HTMLElement,
  clientX: number,
  clientY: number,
  data: DailyMetricsAbsoluteGrowthPoint[],
): string | undefined {
  const bars = container.querySelectorAll(".recharts-bar-rectangle");
  let bestDist = Infinity;
  let bestIndex = -1;
  bars.forEach((el, index) => {
    const box = el.getBoundingClientRect();
    if (box.width === 0) return;
    if (clientY < box.top - 24 || clientY > box.bottom + 48) return;
    const dist = Math.abs(clientX - (box.left + box.width / 2));
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  });
  if (bestIndex < 0 || bestDist > 28) return undefined;
  return ymd(data[bestIndex]?.date);
}

function formatCount(value: number): string {
  return Math.round(Number(value) || 0).toLocaleString("es-CL");
}

function GrowthAbsoluteTooltip({
  active,
  payload,
  label,
  incrementalTooltipLabel,
  cumulativeTooltipLabel,
  barColor,
  lineColor,
}: {
  active?: boolean;
  payload?: Array<{ payload?: DailyMetricsAbsoluteGrowthPoint }>;
  label?: string;
  incrementalTooltipLabel: string;
  cumulativeTooltipLabel: string;
  barColor: string;
  lineColor: string;
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as
    | DailyMetricsAbsoluteGrowthPoint
    | undefined;
  if (!point) return null;

  const rows: Array<{ color: string; text: string }> = [
    {
      color: barColor,
      text: `${incrementalTooltipLabel}: ${formatCount(point.incremental)}`,
    },
    {
      color: lineColor,
      text: `${cumulativeTooltipLabel}: ${formatCount(point.cumulative)}`,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 700,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        {formatChileanDateWithWeekday(point.date) || label}
      </Typography>
      {rows.map((row) => (
        <Typography
          key={row.text}
          variant="caption"
          sx={{ display: "block", color: "text.primary", lineHeight: 1.45 }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: row.color,
              mr: 0.75,
              verticalAlign: "middle",
            }}
          />
          {row.text}
        </Typography>
      ))}
    </Box>
  );
}

export const DailyMetricsGrowthAbsoluteChart: React.FC<Props> = ({
  data,
  height = 160,
  barColor,
  lineColor,
  incrementalLegend,
  cumulativeLegend,
  incrementalTooltipLabel,
  cumulativeTooltipLabel,
  onDayClick,
  hoverAnimate = true,
}) => {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const animateHover = Boolean(onDayClick && hoverAnimate);

  return (
    <Box
      sx={{
        width: "100%",
        height,
        cursor: onDayClick ? "pointer" : undefined,
        outline: "none",
        "&:focus, &:focus-visible, *:focus, *:focus-visible": {
          outline: "none",
        },
        "& .recharts-wrapper, & .recharts-surface": {
          outline: "none",
        },
        "& .recharts-tooltip-cursor": {
          display: "none",
        },
        ...(animateHover
          ? {
              "& .recharts-bar-rectangle": {
                transformBox: "fill-box",
                transformOrigin: "center bottom",
                transition: "transform 160ms ease, filter 160ms ease",
              },
              "& .recharts-active-dot": {
                transformBox: "fill-box",
                transformOrigin: "center",
                animation: "chartDotPop 180ms ease-out",
              },
              "@keyframes chartDotPop": {
                "0%": { transform: "scale(0.45)", opacity: 0.55 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }
          : {}),
      }}
      onClick={
        onDayClick
          ? (event) => {
              const date = dateFromNearestBar(
                event.currentTarget,
                event.clientX,
                event.clientY,
                data,
              );
              if (date) onDayClick(date);
            }
          : undefined
      }
    >
      <ResponsiveContainer>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 8, left: 4, bottom: 8 }}
          onMouseMove={(state) => {
            if (!animateHover) return;
            const index = Number(state.activeIndex ?? state.activeTooltipIndex);
            setHoveredIndex(Number.isInteger(index) ? index : null);
          }}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={(state) => {
            if (!onDayClick) return;
            const fromIndex = dateFromChartIndex(
              data,
              state.activeIndex ?? state.activeTooltipIndex,
            );
            if (fromIndex) {
              onDayClick(fromIndex);
              return;
            }
            const label = state.activeLabel;
            if (typeof label !== "string") return;
            const match = data.find((point) => point.label === label);
            const date = ymd(match?.date);
            if (date) onDayClick(date);
          }}
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
          />
          <YAxis
            yAxisId="incremental"
            orientation="left"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
            tickFormatter={(v) => formatCount(Number(v))}
            width={48}
          />
          <YAxis
            yAxisId="cumulative"
            orientation="right"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
            tickFormatter={(v) => formatCount(Number(v))}
            width={48}
          />
          <RechartsTooltip
            cursor={false}
            {...(onDayClick
              ? {
                  position: { x: 52, y: 4 },
                  wrapperStyle: { pointerEvents: "none" as const },
                }
              : {})}
            content={
              <GrowthAbsoluteTooltip
                incrementalTooltipLabel={incrementalTooltipLabel}
                cumulativeTooltipLabel={cumulativeTooltipLabel}
                barColor={barColor}
                lineColor={lineColor}
              />
            }
          />
          <Legend
            formatter={(value, entry) => (
              <span style={{ color: entry.color }}>
                {value === "incremental" ? incrementalLegend : cumulativeLegend}
              </span>
            )}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar
            yAxisId="incremental"
            dataKey="incremental"
            name="incremental"
            fill={barColor}
            // Avoid SVG path errors from rounded corners on zero-height bars
            radius={data.some((d) => d.incremental === 0) ? 0 : [4, 4, 0, 0]}
            activeBar={false}
            onClick={(item) => {
              if (!onDayClick) return;
              const date = ymd(item?.payload?.date);
              if (date) onDayClick(date);
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`${entry.label}-${index}`}
                fill={
                  entry.isPeakIncremental ? PEAK_BAR_COLOR : barColor
                }
                style={
                  animateHover
                    ? {
                        transformBox: "fill-box",
                        transformOrigin: "center bottom",
                        transition:
                          "transform 160ms ease, filter 160ms ease",
                        transform:
                          hoveredIndex === index ? "scaleY(1.12)" : "scaleY(1)",
                        filter:
                          hoveredIndex === index
                            ? "brightness(1.22)"
                            : undefined,
                      }
                    : undefined
                }
                onClick={() => {
                  const date = ymd(entry.date);
                  if (date) onDayClick?.(date);
                }}
              />
            ))}
          </Bar>
          <Line
            yAxisId="cumulative"
            type="monotone"
            dataKey="cumulative"
            name="cumulative"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            activeDot={
              onDayClick
                ? { r: 6, fill: lineColor, stroke: theme.palette.background.paper, strokeWidth: 2 }
                : { r: 4, fill: lineColor }
            }
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
